import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Breadcrumbs, { breadcrumbLd } from "@/components/Breadcrumbs";
import { CITIES, cityPath } from "@/lib/cities";

const crumbs = [{ label: "Home", to: "/" }, { label: "Service Areas" }];
const COUNTIES = ["Charleston County", "Berkeley County", "Dorchester County", "Colleton County"];

export default function ServiceAreasHub() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => CITIES.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())), [q]);

  return (
    <>
      <Seo path="/service-areas" title="Service Areas" description="Skedaddle Junk Removal serves 30 towns across Charleston, Berkeley and Dorchester counties. Find your Lowcountry town and get a free quote." jsonLd={breadcrumbLd(crumbs)} />
      <PageHero eyebrow="Service areas" title="We cover the whole Lowcountry." subtitle="Thirty towns across three counties, from downtown Charleston to the barrier islands. Find yours!">
        <Breadcrumbs items={crumbs} />
      </PageHero>

      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <div className="flex items-center gap-3 bg-cream border-2 border-charcoal rounded-full px-5 py-4 shadow-brut max-w-xl">
            <Search className="w-5 h-5" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your town..." data-testid="area-search-input"
              className="w-full bg-transparent font-body font-semibold focus:outline-none placeholder:text-charcoal/40" />
          </div>

          {q ? (
            <div className="mt-8 flex flex-wrap gap-3" data-testid="area-search-results">
              {filtered.length ? filtered.map((c) => (
                <Link key={c.slug} to={cityPath(c)} className="inline-flex items-center gap-2 bg-white border-2 border-charcoal rounded-full px-4 py-2 font-body font-semibold shadow-brutsm hover:bg-teal hover:text-cream transition-colors">
                  <MapPin className="w-4 h-4" /> {c.name}
                </Link>
              )) : <p className="font-body text-charcoal/60">No match, but give us a call! We likely still cover you.</p>}
            </div>
          ) : (
            <div className="mt-12 space-y-12">
              {COUNTIES.map((county) => {
                const list = CITIES.filter((c) => c.county === county);
                if (!list.length) return null;
                return (
                  <div key={county}>
                    <h2 className="font-heading font-bold text-2xl md:text-3xl mb-5">{county}</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {list.map((c) => (
                        <Link key={c.slug} to={cityPath(c)} data-testid={`area-city-${c.slug}`}
                          className="group flex items-center gap-3 bg-white border-2 border-charcoal rounded-2xl px-5 py-4 shadow-brut hover:bg-sunny transition-colors">
                          <div className="w-10 h-10 rounded-xl border-2 border-charcoal bg-teal text-cream flex items-center justify-center group-hover:bg-coral"><MapPin className="w-5 h-5" /></div>
                          <span className="font-heading font-bold text-lg">{c.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
